import * as React from 'react';
import { MoreHorizontalIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { buttonVariants } from '../../components/ui/button';

function Pagination({ className, ...props }) {
  return (
    <nav
      role="navigation"
      aria-label="pagination"
      className={cn('mx-auto flex w-full justify-center px-2 sm:px-0', className)}
      {...props}
    />
  );
}

function PaginationContent({ className, ...props }) {
  return (
    <ul className={cn('flex items-center gap-1 text-sm sm:text-base flex-wrap justify-center', className)} {...props} />
  );
}

function PaginationItem(props) {
  return <li {...props} />;
}

function PaginationLink({ className, isActive, size = 'icon', ...props }) {
  return (
    <button
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        buttonVariants({ variant: 'ghost', size }),
        'rounded-full min-w-[32px] sm:min-w-[40px] text-xs sm:text-sm px-2 sm:px-3 py-1',
        {
          'bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)]': isActive,
        },
        className,
      )}
      {...props}
    />
  );
}

function PaginationPrevious({ className, disabled, ...props }) {
  return (
    <PaginationLink
      aria-label="Go to previous page"
      size="default"
      aria-disabled={disabled}
      disabled={disabled}
      className={cn(
        'gap-1 border rounded-full px-2.5 sm:px-3 py-1 text-xs sm:text-sm',
        disabled && 'opacity-50 pointer-events-none',
        className,
      )}
      {...props}
    >
      <span>Prev</span>
    </PaginationLink>
  );
}

function PaginationNext({ className, disabled, ...props }) {
  return (
    <PaginationLink
      aria-label="Go to next page"
      size="default"
      aria-disabled={disabled}
      disabled={disabled}
      className={cn(
        'gap-1 border rounded-full px-2.5 sm:px-3 py-1 text-xs sm:text-sm',
        disabled && 'opacity-50 pointer-events-none',
        className,
      )}
      {...props}
    >
      <span>Next</span>
    </PaginationLink>
  );
}

function PaginationEllipsis({ className, ...props }) {
  return (
    <span
      aria-hidden
      className={cn('flex size-7 sm:size-9 items-center justify-center text-sm sm:text-base', className)}
      {...props}
    >
      <MoreHorizontalIcon className="size-4 sm:size-5" />
      <span className="sr-only">More pages</span>
    </span>
  );
}

const PackPagination = ({ totalPages, activePage, onPageClick }) => {
  const handlePageClick = (page) => () => {
    if (page >= 1 && page <= totalPages && page !== activePage) {
      onPageClick(page);
    }
  };

  // ✅ Reset only on mount (when tab/component is loaded fresh)
  React.useEffect(() => {
    onPageClick(1);
    // run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (totalPages <= 1) return null;

  const pagesToShow = [];

  if (activePage > 1) pagesToShow.push(activePage - 1);
  pagesToShow.push(activePage);
  if (activePage < totalPages) pagesToShow.push(activePage + 1);

  const showLastPage = totalPages > 3 && !pagesToShow.includes(totalPages);

  return (
    <Pagination>
      <PaginationContent>
        {/* Prev */}
        <PaginationItem>
          <PaginationPrevious onClick={handlePageClick(activePage - 1)} disabled={activePage === 1} />
        </PaginationItem>

        {/* Dynamic Pages */}
        {pagesToShow.map((page) => (
          <PaginationItem key={page}>
            <PaginationLink onClick={handlePageClick(page)} isActive={page === activePage}>
              {page}
            </PaginationLink>
          </PaginationItem>
        ))}

        {/* Last Page */}
        {showLastPage && (
          <>
            <PaginationItem>
              <PaginationEllipsis />
            </PaginationItem>
            <PaginationItem>
              <PaginationLink onClick={handlePageClick(totalPages)} isActive={activePage === totalPages}>
                {totalPages}
              </PaginationLink>
            </PaginationItem>
          </>
        )}

        {/* Next */}
        <PaginationItem>
          <PaginationNext onClick={handlePageClick(activePage + 1)} disabled={activePage === totalPages} />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
};

export default PackPagination;
